import React, { useEffect, useState } from 'react';
import RepresentativeLayout from '../../components/layout/RepresentativeLayout';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Users,
  FileText,
  DollarSign,
  TrendingUp,
  Clock,
  CheckCircle2,
  ArrowUpRight,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import { representativeAPI } from '../../services/api';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import logo from '../../assets/pictures/logo.png';
import serviceImage2 from '../../assets/background-welcome.png';

export default function RepDashboardMain() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState({
    totalStudents: 0,
    activeStudents: 0,
    totalServiceRequests: 0,
    activeServiceRequests: 0,
    completedServiceRequests: 0,
    totalEarnings: 0,
    pendingEarnings: 0,
    commissionPercentage: 0,
    representativeLevel: 1,
  });

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await representativeAPI.getDashboard();
      setDashboard(response.dashboard);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const repLevel = user?.role === 'rep1' ? 1 : 2;

  const statCards = [
    {
      title: 'Total Students',
      value: dashboard.totalStudents,
      subtitle: `${dashboard.activeStudents} active`,
      icon: Users,
      color: 'from-blue-500 to-cyan-400',
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-600',
    },
    {
      title: 'Service Requests',
      value: dashboard.totalServiceRequests,
      subtitle: `${dashboard.activeServiceRequests} in progress`,
      icon: FileText,
      color: 'from-violet-500 to-purple-400',
      bgColor: 'bg-violet-50',
      textColor: 'text-violet-600',
    },
    {
      title: 'Total Earnings',
      value: `$${dashboard.totalEarnings.toLocaleString()}`,
      subtitle: `$${dashboard.pendingEarnings.toLocaleString()} pending`,
      icon: DollarSign,
      color: 'from-emerald-500 to-teal-400',
      bgColor: 'bg-emerald-50',
      textColor: 'text-emerald-600',
    },
    {
      title: 'Commission Rate',
      value: `${dashboard.commissionPercentage}%`,
      subtitle: `Level ${repLevel} Representative`,
      icon: TrendingUp,
      color: 'from-amber-500 to-orange-400',
      bgColor: 'bg-amber-50',
      textColor: 'text-amber-600',
    },
  ];

  if (loading) {
    return (
      <RepresentativeLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </RepresentativeLayout>
    );
  }

  return (
    <RepresentativeLayout>
      <div className="space-y-6">
        {/* Welcome */}
        <div className="partner-card partner-card-hover relative overflow-hidden rounded-[30px] border border-[#8DC7FF]/45 bg-gradient-to-br from-[#e4f0ff] via-[#d2e6ff] to-[#bddbff] p-4 shadow-[0_10px_30px_rgba(111,168,255,0.24)] lg:p-5">
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(111,168,255,0.18),transparent_34%),radial-gradient(circle_at_bottom_left,rgba(111,168,255,0.12),transparent_30%)]" />
          <div className="relative grid gap-5 lg:grid-cols-[1.15fr_0.85fr] lg:items-center">
            <div className="relative overflow-hidden rounded-[24px] bg-white/18 p-6 backdrop-blur-sm">
              <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-white/40 bg-white/40 px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-[#4A8CF1]">
                <img
                  src={logo}
                  alt="Fly8"
                  className="h-4 w-auto object-contain"
                />
                Representative Workspace
              </div>
              <h1 className="text-3xl font-bold tracking-tight text-[#3F7EDC] lg:text-[2.1rem]">
                Welcome back, {user?.firstName}!
              </h1>
              <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-700">
                Here's an overview of your referred students and commissions.
              </p>
            </div>
            <div className="relative h-full min-h-[260px] overflow-hidden rounded-[24px]">
              <img
                src={serviceImage2}
                alt="Representative Dashboard"
                className="h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0.02),rgba(255,255,255,0.16))]" />
              <div className="absolute bottom-4 left-4 rounded-full border border-white/40 bg-white/55 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#4A8CF1] backdrop-blur-md">
                Representative Workspace
              </div>
            </div>
          </div>
        </div>

        {/* Stat Cards */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {statCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <motion.div
                key={stat.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="partner-card partner-card-hover rounded-[24px] border-[#8DC7FF]/30 bg-[linear-gradient(135deg,rgba(228,240,255,0.82),rgba(210,230,255,0.58))] p-5 shadow-[0_12px_28px_rgba(111,168,255,0.18)]">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-600">
                        {stat.title}
                      </p>
                      <p className="mt-1 text-2xl font-bold text-slate-900">
                        {stat.value}
                      </p>
                      <p className="mt-1 text-xs text-slate-600">
                        {stat.subtitle}
                      </p>
                    </div>
                    <div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-[#8DC7FF]/38 bg-[linear-gradient(135deg,rgba(111,168,255,0.18),rgba(111,168,255,0.08))]">
                      <Icon className="h-5 w-5 text-[#4A8CF1]" />
                    </div>
                  </div>
                </Card>
              </motion.div>
            );
          })}
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Recent Service Requests */}
          <Card className="partner-card rounded-[28px] border-[#8DC7FF]/28 bg-[linear-gradient(135deg,rgba(228,240,255,0.72),rgba(210,230,255,0.42))] p-6 shadow-[0_12px_28px_rgba(111,168,255,0.14)]">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900">
                Recent Service Requests
              </h3>
              <button
                onClick={() => navigate('/representative/service-requests')}
                className="flex items-center gap-1 text-sm font-medium text-[#3F7EDC]"
              >
                View All <ArrowUpRight className="w-3 h-3" />
              </button>
            </div>
            <div className="space-y-3">
              {dashboard.activeServiceRequests === 0 ? (
                <div className="text-center py-8">
                  <FileText className="mx-auto mb-2 h-10 w-10 text-[#4A8CF1]" />
                  <p className="text-sm text-slate-600">
                    No active service requests
                  </p>
                </div>
              ) : (
                <div className="flex items-center gap-4 rounded-[22px] border border-[#8DC7FF]/26 bg-white/42 p-3">
                  <Clock className="w-5 h-5 text-amber-500" />
                  <div>
                    <p className="text-sm font-medium text-slate-900">
                      {dashboard.activeServiceRequests} Active Requests
                    </p>
                    <p className="text-xs text-slate-500">
                      {dashboard.completedServiceRequests} completed
                    </p>
                  </div>
                </div>
              )}
            </div>
          </Card>

          {/* Commission Summary */}
          <Card className="partner-card rounded-[28px] border-[#8DC7FF]/28 bg-[linear-gradient(135deg,rgba(228,240,255,0.72),rgba(210,230,255,0.42))] p-6 shadow-[0_12px_28px_rgba(111,168,255,0.14)]">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900">
                Commission Summary
              </h3>
              <button
                onClick={() => navigate('/representative/commissions')}
                className="flex items-center gap-1 text-sm font-medium text-[#3F7EDC]"
              >
                View All <ArrowUpRight className="w-3 h-3" />
              </button>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between rounded-[22px] border border-[#8DC7FF]/26 bg-white/42 p-3">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                  <span className="text-sm font-medium">Total Earned</span>
                </div>
                <span className="font-bold text-[#3F7EDC]">
                  ${dashboard.totalEarnings.toLocaleString()}
                </span>
              </div>
              <div className="flex items-center justify-between rounded-[22px] border border-[#8DC7FF]/26 bg-white/42 p-3">
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-amber-500" />
                  <span className="text-sm font-medium">Pending</span>
                </div>
                <span className="font-bold text-[#3F7EDC]">
                  ${dashboard.pendingEarnings.toLocaleString()}
                </span>
              </div>
            </div>
          </Card>
        </div>

        {/* Read-Only Notice */}
        <Card className="partner-card rounded-[24px] border-[#8DC7FF]/28 bg-[linear-gradient(135deg,rgba(228,240,255,0.64),rgba(210,230,255,0.34))] p-4 shadow-[0_10px_24px_rgba(111,168,255,0.12)]">
          <div className="flex items-start gap-3">
            <Badge
              variant="outline"
              className="mt-0.5 border-[#8DC7FF]/40 text-[#3F7EDC]"
            >
              Level {repLevel}
            </Badge>
            <div>
              <p className="text-sm font-medium text-slate-900">
                Read-Only Access
              </p>
              <p className="mt-0.5 text-xs text-slate-600">
                As a Level {repLevel} Representative, you can view student
                progress, track service requests, and monitor your commissions.
                Contact the assigned counselor via messages for any updates.
              </p>
            </div>
          </div>
        </Card>
      </div>
    </RepresentativeLayout>
  );
}
